import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { useSEO, breadcrumb } from "../lib/seo";
import { RatingStars, RatingSources, SoftwareLogo } from "../components/SoftwareCard";
import { Check, X, ExternalLink, DollarSign, Target, Puzzle } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long" });
  } catch {
    return null;
  }
}

export default function SoftwareDetail() {
  const { slug } = useParams();
  const [sw, setSw] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setSw(null);
    api.get(`/software/${slug}`).then(({ data }) => setSw(data)).catch(() => setNotFound(true));
  }, [slug]);

  const graph = sw
    ? [
        {
          "@type": "Product",
          name: sw.name,
          description: sw.tagline,
          brand: { "@type": "Brand", name: sw.name },
          url: sw.website || undefined,
        },
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "Reviews", path: "/software" },
          { name: sw.name, path: `/software/${sw.slug}` },
        ]),
      ]
    : null;
  if (sw && sw.faq && sw.faq.length) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: sw.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }
  useSEO({
    title: sw?.seo_title || sw?.name,
    description: sw?.seo_description || sw?.tagline,
    jsonLd: graph,
  });

  if (notFound) return <div className="max-w-3xl mx-auto px-4 py-32 text-center text-slate-500">Review not found. <Link to="/software" className="text-sky-600 underline">Browse all reviews</Link></div>;
  if (!sw) return <div className="max-w-3xl mx-auto px-4 py-32 text-center text-slate-400">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16" data-testid="software-detail-page">
      <nav className="text-xs text-slate-400 mb-8">
        <Link to="/" className="hover:text-sky-600">Home</Link> / <Link to="/software" className="hover:text-sky-600">Reviews</Link> / <span className="text-slate-600">{sw.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          <div className="flex items-start gap-5">
            <SoftwareLogo software={sw} size="w-16 h-16" />
            <div>
              <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900">{sw.name} Review</h1>
              <p className="mt-2 text-slate-600">{sw.tagline}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
                <RatingStars rating={sw.rating} size={5} reviewTotal={sw.review_total} />
                {sw.ratings?.length ? <span className="text-xs text-slate-500">aggregate across {sw.ratings.length} sources</span> : null}
                {fmtDate(sw.updated_at) && <span className="text-sm text-slate-400">Updated {fmtDate(sw.updated_at)}</span>}
              </div>
              <p className="mt-2 text-xs text-slate-400 leading-relaxed">
                Reviewed by the <Link to="/methodology" className="text-slate-500 underline hover:text-sky-600">Pool Software Guide editorial team</Link> — pricing from the vendor's official page; user ratings aggregated from Capterra, G2 and other review platforms (see “Ratings across the web”).
              </p>
            </div>
          </div>

          {/* Verdict */}
          <div className="mt-10 bg-sky-50 border border-sky-100 rounded-2xl p-6" data-testid="verdict-card">
            <h2 className="font-heading font-bold text-slate-900 text-sm uppercase tracking-[0.15em] mb-2">Bottom Line</h2>
            <p className="text-slate-700 leading-relaxed">{sw.verdict}</p>
          </div>

          {/* Pros / Cons */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-border p-6">
              <h3 className="font-heading font-bold text-emerald-700 mb-4">Pros</h3>
              <ul className="space-y-3">
                {(sw.pros || []).map((p) => (
                  <li key={p} className="flex gap-2.5 text-sm text-slate-700 leading-relaxed">
                    <Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" aria-hidden="true" /> {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-2xl border border-border p-6">
              <h3 className="font-heading font-bold text-rose-700 mb-4">Cons</h3>
              <ul className="space-y-3">
                {(sw.cons || []).map((c) => (
                  <li key={c} className="flex gap-2.5 text-sm text-slate-700 leading-relaxed">
                    <X className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" aria-hidden="true" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Full review */}
          <article className="prose-pool mt-12" data-testid="review-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{sw.description}</ReactMarkdown>
          </article>

          {/* FAQ */}
          {sw.faq && sw.faq.length > 0 && (
            <div className="mt-12 border-t border-border pt-8" data-testid="faq-section">
              <h2 className="font-heading font-bold text-slate-900 text-xl mb-5">Frequently asked questions</h2>
              <div className="space-y-4">
                {sw.faq.map((f, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-border p-5">
                    <h3 className="font-heading font-bold text-slate-900 text-sm">{f.q}</h3>
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Keep comparing — internal linking */}
          <div className="mt-12 border-t border-border pt-8">
            <h2 className="font-heading font-bold text-slate-900 text-sm uppercase tracking-[0.15em] mb-4">Keep comparing</h2>
            <div className="flex flex-wrap gap-2">
              <Link to="/best-pool-service-software" className="text-sm px-4 py-2 rounded-full bg-slate-900 text-white font-semibold hover:bg-slate-700 transition-colors duration-200">
                See the full 2026 ranking
              </Link>
              <Link to="/comparisons" className="text-sm px-4 py-2 rounded-full border border-border bg-white text-slate-700 font-semibold hover:border-sky-400 hover:text-sky-600 transition-colors duration-200">
                Head-to-head comparisons
              </Link>
              {(sw.category_slugs || []).slice(0, 3).map((c) => (
                <Link key={c} to={`/category/${c}`} className="text-sm px-4 py-2 rounded-full border border-border bg-white text-slate-700 font-medium hover:border-sky-400 hover:text-sky-600 transition-colors duration-200">
                  {c.replace(/-/g, " ")}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <div className="bg-white rounded-2xl border border-border p-6 sticky top-24">
            <div className="flex items-start gap-3 mb-5">
              <DollarSign className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">Starting at</p>
                <p className="font-heading font-bold text-2xl text-slate-900" data-testid="pricing-start">{sw.pricing_start}</p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{sw.pricing_details}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 mb-5">
              <Target className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">Best for</p>
                <p className="text-sm text-slate-700 leading-relaxed">{sw.best_for}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Puzzle className="w-5 h-5 text-sky-500 shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">Integrations</p>
                <p className="text-sm text-slate-700 leading-relaxed">{(sw.integrations || []).join(", ")}</p>
              </div>
            </div>
            {sw.website && (
              <>
                <a href={sw.website} target="_blank" rel="sponsored nofollow noopener noreferrer" data-testid="visit-website-button"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-sky-500 text-white text-sm font-semibold hover:bg-sky-600 active:scale-95 transition-colors duration-200">
                  Visit {sw.name} <ExternalLink className="w-4 h-4" aria-hidden="true" />
                </a>
                <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
                  Some links are affiliate links; we may earn a commission at no cost to you. This never affects our ratings — see our <Link to="/methodology" className="underline hover:text-sky-600">methodology</Link>.
                </p>
              </>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-border p-6">
            <h3 className="font-heading font-bold text-slate-900 text-sm mb-4">Key features</h3>
            <div className="flex flex-wrap gap-2">
              {(sw.features || []).map((f) => (
                <span key={f} className="text-xs px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 font-medium">{f}</span>
              ))}
            </div>
          </div>

          {sw.ratings?.length > 0 && (
            <div className="bg-white rounded-2xl border border-border p-6" data-testid="ratings-across-web">
              <h3 className="font-heading font-bold text-slate-900 text-sm mb-1">Ratings across the web</h3>
              <p className="text-xs text-slate-400 mb-4">Real scores from independent review platforms — click to verify.</p>
              <RatingSources ratings={sw.ratings} />
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
